import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  EditLocation,
  Checkbox
} from "@mui/material";
import {
  AccessTime,
  Person,
  CheckCircle,
  Cancel,
  Schedule,
  BeachAccess,
  MyLocation,
  Map,
  Settings,
  Refresh,
  Download,
  Edit,
  Visibility,
  TrendingUp,
  Assessment,
  FilterList,
  DateRange,
  LocationOn,
  Save as SaveIcon,
  Add as AddIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
  Close
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import io from "socket.io-client";
import axios from 'axios';
import { fetchJsonFromApi, buildApiUrl } from "../config/api";
import { mediaUrl, getSchoolLogoUrl, getStudentThumbnailUrl, getStudentFullImageUrl } from '../utils/mediaUrls';

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/** Teacher docs from the API use lastname/othernames; some paths may set name */
function teacherDisplayName(t) {
  if (t == null || t === "") return "Staff";
  if (typeof t.name === "string" && t.name.trim() && t.name !== "undefined") {
    return t.name.trim();
  }
  const n = `${t.lastname || ""} ${t.othernames || ""}`.trim();
  return n || "Staff";
}

function findTeacherById(teachers, staffId) {
  if (!teachers || staffId == null) return undefined;
  const sid = String(staffId);
  return teachers.find(
    (x) =>
      String(x._id) === sid ||
      (x.id != null && String(x.id) === sid) ||
      (x.ID != null && String(x.ID) === sid)
  );
}

/** Prefer name returned by mark-manual (JSON staffName); fallback to Redux teachers list */
function displayNameAfterMarkManual(axiosResponse, teachers, staffId) {
  const body = axiosResponse && axiosResponse.data;
  if (!body || typeof body !== "object") {
    return teacherDisplayName(findTeacherById(teachers, staffId)) || "Staff";
  }
  let fromApi = body.staffName;
  if (fromApi != null && typeof fromApi !== "string") {
    fromApi = String(fromApi);
  }
  if (
    typeof fromApi === "string" &&
    fromApi.trim() &&
    fromApi.trim() !== "undefined"
  ) {
    return fromApi.trim();
  }
  const fromList = teacherDisplayName(findTeacherById(teachers, staffId));
  return fromList && String(fromList).trim() ? String(fromList).trim() : "Staff";
}

/** Real-time events may omit staffName; avoid "undefined" in snackbars */
function socketEventStaffLabel(data) {
  if (!data || typeof data !== "object") return "A staff member";
  const s = data.staffName ?? data.name;
  if (typeof s === "string" && s.trim() && s.trim() !== "undefined") return s.trim();
  return "A staff member";
}

function StaffAttendance() {
  const dispatch = useDispatch();
  const { admin, teachers, schooldetails } = useSelector((state) => state);

  /** Redux/localStorage may store workers as a nested array */
  const teacherList = useMemo(() => {
    if (!teachers) return [];
    if (Array.isArray(teachers) && teachers.length > 0 && Array.isArray(teachers[0])) {
      return teachers[0];
    }
    return Array.isArray(teachers) ? teachers : [];
  }, [teachers]);
  
  // State management
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({});
  const [availableFilters, setAvailableFilters] = useState({});
  const [socket, setSocket] = useState(null);
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);
  
  // GPS Location states
  const [locationPermission, setLocationPermission] = useState("prompt");
  const [useGPS, setUseGPS] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationAddress, setLocationAddress] = useState("");
  const [locationAccuracy, setLocationAccuracy] = useState(0);
  const [locationError, setLocationError] = useState(null);
  
  // Environment detection
  const [isElectron, setIsElectron] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Dialog states
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  
  // Report filter states
  const [reportFilter, setReportFilter] = useState("week");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStaff, setSelectedStaff] = useState("");
  
  // Manual attendance states
  const [selectedStaffForManual, setSelectedStaffForManual] = useState("");
  const [manualStatus, setManualStatus] = useState("Present");
  const [manualCheckIn, setManualCheckIn] = useState(new Date());
  const [manualCheckOut, setManualCheckOut] = useState(new Date());
  const [manualRemarks, setManualRemarks] = useState("");
  
  // School location states
  const [schoolLat, setSchoolLat] = useState("");
  const [schoolLng, setSchoolLng] = useState("");
  const [schoolAddress, setSchoolAddress] = useState("");
  const [allowedRadius, setAllowedRadius] = useState(100);
  const [workingHours, setWorkingHours] = useState({ start: "08:00", end: "17:00" });
  const [lateThreshold, setLateThreshold] = useState(15);
  const [autoAttendance, setAutoAttendance] = useState(true);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Filter states
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null
  });
  const [staffFilter, setStaffFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Attendance Records states
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);

  // Add new states for individual manual marking
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [markingLoading, setMarkingLoading] = useState({});
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [selectedStaffForBulk, setSelectedStaffForBulk] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Add new state for dashboard table filtering
  const [dashboardFilter, setDashboardFilter] = useState({
    status: '',
    department: '',
    search: ''
  });

  // Add filtered records for dashboard
  const [filteredDashboardRecords, setFilteredDashboardRecords] = useState([]);

  // Add new state for view details dialog
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Add new state for enhanced view details
  const [viewDetailsFilter, setViewDetailsFilter] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  const [filteredStaffRecords, setFilteredStaffRecords] = useState([]);
  const [staffAttendanceHistory, setStaffAttendanceHistory] = useState([]);

  // Detect environment on component mount
  useEffect(() => {
    // Check if running in Electron
    const electron = window.require && window.require('electron');
    setIsElectron(!!electron);
    
    // Check online status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Enhanced GPS Location functions that work in both Electron and web browsers
  const requestLocationPermission = useCallback(async () => {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported in this environment.");
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    try {
      // For Electron, we need to ensure location services are enabled
      if (isElectron) {
        // Check if we're in a secure context (required for geolocation)
        if (!window.isSecureContext && window.location.protocol !== 'https:') {
          setLocationError("Location services require a secure context (HTTPS) or localhost in Electron.");
          setLocationLoading(false);
          return;
        }
      }

      // Check if permission is already granted
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission?.state === 'denied') {
          setLocationError("Location access denied. Please enable location permissions in your browser settings and refresh the page.");
          setLocationPermission("denied");
          setLocationLoading(false);
          return;
        }
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: isElectron ? 20000 : 15000, // Longer timeout for Electron
            maximumAge: 300000 // 5 minutes
          }
        );
      });

      const { latitude, longitude, accuracy } = position.coords;
      setCurrentLocation({
        latitude,
        longitude,
        accuracy,
        timestamp: Date.now()
      });
      setLocationAccuracy(accuracy);
      setLocationPermission("granted");

      // Set a simple address format
        setLocationAddress(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      
      // Show success message
      setSnackbar({ 
        open: true, 
        message: `Location obtained successfully! (${isElectron ? 'Electron' : 'Browser'})`, 
        severity: "success" 
      });

    } catch (error) {
      console.error("Error getting location:", error);
      
      let errorMessage = "Unable to get your location. ";
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage += isElectron 
            ? "Location access denied. Please enable location services in your system settings and restart the application."
            : "Location access denied. Please enable location permissions in your browser settings and refresh the page.";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage += isElectron
            ? "Location information is unavailable. Please check your system's location services and ensure the app has location permissions."
            : "Location information is unavailable. Please check your device's location services.";
          break;
        case error.TIMEOUT:
          errorMessage += "Location request timed out. Please try again.";
          break;
        default:
          errorMessage += "An unknown error occurred while retrieving location.";
      }
      
      setLocationError(errorMessage);
      setLocationPermission("denied");
      
      // Show error message
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: "error" 
      });
    } finally {
      setLocationLoading(false);
    }
  }, [isElectron]);

  // Fallback function for manual location entry
  const enableManualLocationEntry = useCallback(() => {
    setLocationPermission("manual");
    setLocationError(null);
    setSnackbar({ 
      open: true, 
      message: "Manual location entry enabled. You can enter coordinates manually.", 
      severity: "info" 
    });
  }, []);

  // Check location permission on component mount
  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state);
        if (result.state === 'granted' && useGPS) {
          requestLocationPermission();
        }
      });
    }
  }, [useGPS, requestLocationPermission]);

  // Initialize GPS location on component mount
  useEffect(() => {
    if (useGPS) {
      requestLocationPermission();
    }
  }, [useGPS, requestLocationPermission]);

  // Initialize socket connection for real-time updates
  useEffect(() => {
    if (realTimeUpdates && admin?.schoolID) {
      const newSocket = io("https://servermain.sisrevolution.com", {
        query: { schoolID: admin.schoolID, userType: "admin" },
        timeout: 5000, // Add timeout
        forceNew: true // Force new connection
      });
      
      newSocket.on("connect", () => {
        console.log("Connected to real-time updates");
        setSnackbar({ open: true, message: "Connected to real-time updates", severity: "success" });
      });
      
      newSocket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
        setSnackbar({ open: true, message: "Failed to connect to real-time updates", severity: "warning" });
      });
      
      newSocket.on("attendanceUpdate", (data) => {
        console.log("Real-time attendance update:", data);
        fetchAttendanceData();
        setSnackbar({ open: true, message: "Attendance updated in real-time", severity: "info" });
      });
      
      newSocket.on("staffCheckIn", (data) => {
        console.log("Staff checked in:", data);
        fetchAttendanceData();
        setSnackbar({
          open: true,
          message: `${socketEventStaffLabel(data)} checked in`,
          severity: "success"
        });
      });
      
      newSocket.on("staffCheckOut", (data) => {
        console.log("Staff checked out:", data);
        fetchAttendanceData();
        setSnackbar({
          open: true,
          message: `${socketEventStaffLabel(data)} checked out`,
          severity: "info"
        });
      });
      
      setSocket(newSocket);
      
      return () => {
        newSocket.disconnect();
      };
    }
  }, [realTimeUpdates, admin?.schoolID]);

  // Update the check-out function to handle the case where there's no check-in
  const markStaffCheckOut = async (staffId) => {
    const currentTime = new Date().toISOString();
    
    setMarkingLoading(prev => ({ ...prev, [staffId]: true }));
    
    try {
      // First, check if there's already a record for today
      const todayRecords = getTodayAttendanceRecords();
      const existingRecord = todayRecords.find(
        (record) => String(record.staffId?._id) === String(staffId)
      );
      
      if (existingRecord && existingRecord.checkInTime) {
        // Update existing record with check-out time
        const response = await axios.post(buildApiUrl("/api/attendance/mark-manual"), {
          schoolID: admin.schoolID,
          staffId: staffId,
          date: selectedDate,
          status: existingRecord.status, // Keep existing status
          checkInTime: existingRecord.checkInTime, // Keep existing check-in time
          checkOutTime: currentTime,
          attendanceType: "Manual",
          remarks: `Manual check-out on ${selectedDate}`
        });
        
        if (response.status === 200) {
          const displayName = displayNameAfterMarkManual(response, teacherList, staffId);
          setSnackbar({ 
            open: true, 
            message: `Check-out recorded for ${displayName}`,
            severity: "success" 
          });
          
          fetchAttendanceData();
          
          if (socket) {
            socket.emit("staffCheckOut", {
              schoolID: admin.schoolID,
              staffId: staffId,
              staffName: displayName,
              date: selectedDate,
              checkOutTime: currentTime
            });
          }
        }
      } else {
        // No existing record or no check-in time, create a new record with both check-in and check-out
        const response = await axios.post(buildApiUrl("/api/attendance/mark-manual"), {
          schoolID: admin.schoolID,
          staffId: staffId,
          date: selectedDate,
          status: "Present",
          checkInTime: currentTime, // Use current time as check-in
          checkOutTime: currentTime, // Use current time as check-out
          attendanceType: "Manual",
          remarks: `Manual check-out (no previous check-in) on ${selectedDate}`
        });
        
        if (response.status === 200) {
          const displayName = displayNameAfterMarkManual(response, teacherList, staffId);
          setSnackbar({ 
            open: true, 
            message: `Check-out recorded for ${displayName}`,
            severity: "success" 
          });
          
          fetchAttendanceData();
          
          if (socket) {
            socket.emit("staffCheckOut", {
              schoolID: admin.schoolID,
              staffId: staffId,
              staffName: displayName,
              date: selectedDate,
              checkOutTime: currentTime
            });
          }
        }
      }
    } catch (error) {
      console.error("Error recording check-out:", error);
      setSnackbar({ 
        open: true, 
        message: "Error recording check-out", 
        severity: "error" 
      });
    } finally {
      setMarkingLoading(prev => ({ ...prev, [staffId]: false }));
    }
  };

  // Update the check-in function to be simpler
  const markStaffCheckIn = async (staffId) => {
    const currentTime = new Date().toISOString();
    
    setMarkingLoading(prev => ({ ...prev, [staffId]: true }));
    
    try {
      const response = await axios.post(buildApiUrl("/api/attendance/mark-manual"), {
        schoolID: admin.schoolID,
        staffId: staffId,
        date: selectedDate,
        status: "Present",
        checkInTime: currentTime,
        attendanceType: "Manual",
        remarks: `Manual check-in on ${selectedDate}`
      });
      
      if (response.status === 200) {
        const displayName = displayNameAfterMarkManual(response, teacherList, staffId);
        setSnackbar({ 
          open: true, 
          message: `Check-in recorded for ${displayName}`,
          severity: "success" 
        });
        
        fetchAttendanceData();
        
        if (socket) {
          socket.emit("staffCheckIn", {
            schoolID: admin.schoolID,
            staffId: staffId,
            staffName: displayName,
            date: selectedDate,
            checkInTime: currentTime
          });
        }
      }
    } catch (error) {
      console.error("Error recording check-in:", error);
      setSnackbar({ 
        open: true, 
        message: "Error recording check-in", 
        severity: "error" 
      });
    } finally {
      setMarkingLoading(prev => ({ ...prev, [staffId]: false }));
    }
  };

  // Update the markStaffAttendance function to work exactly like markStaffCheckIn
  const markStaffAttendance = async (staffId, status) => {
    const currentTime = new Date().toISOString();
    
    setMarkingLoading(prev => ({ ...prev, [staffId]: true }));
    
    try {
      const response = await axios.post(buildApiUrl("/api/attendance/mark-manual"), {
        schoolID: admin.schoolID,
        staffId: staffId,
        date: selectedDate,
        status: "Absent",
        checkInTime: currentTime,
        attendanceType: "Manual",
        remarks: `Attendance marked as ${status} on ${selectedDate}`
      });
      
      if (response.status === 200) {
        const displayName = displayNameAfterMarkManual(response, teacherList, staffId);
        setSnackbar({ 
          open: true, 
          message: `Staff marked as ${status} for ${displayName}`,
          severity: "success" 
        });
        
        fetchAttendanceData();
        
        if (socket) {
          socket.emit("staffAttendanceMarked", {
            schoolID: admin.schoolID,
            staffId: staffId,
            staffName: displayName,
            status: status,
            date: selectedDate
          });
        }
      }
    } catch (error) {
      console.error("Error marking staff attendance:", error);
      setSnackbar({ 
        open: true, 
        message: "Error marking attendance", 
        severity: "error" 
      });
    } finally {
      setMarkingLoading(prev => ({ ...prev, [staffId]: false }));
    }
  };

  // Update the data fetching function
  const fetchAttendanceData = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    setLoading(true);
    try {
      const schoolSeg = encodeURIComponent(String(admin.schoolID).trim());
      const data = await fetchJsonFromApi(
        `/api/attendance/school/${schoolSeg}/attendance`
      );
      
      if (data.success) {
        // The API returns { staffAttendance: [...], summary: {...} }
        // We need to flatten the attendance records for easier handling
        const flattenedRecords = [];
        
        if (data.data.staffAttendance) {
          data.data.staffAttendance.forEach(staff => {
            if (staff.attendanceRecords && staff.attendanceRecords.length > 0) {
              staff.attendanceRecords.forEach(record => {
                flattenedRecords.push({
                  ...record,
                  staffId: {
                    _id: staff.staffId,
                    name: staff.staffName,
                    position: staff.position,
                    email: staff.staffEmail
                  }
                });
              });
            }
          });
        }
        
        setAttendanceData(flattenedRecords);
      } else {
        setSnackbar({ open: true, message: "Failed to fetch attendance data", severity: "error" });
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setSnackbar({ open: true, message: "Error fetching attendance data", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [admin?.schoolID]);

  // Add a helper function to get today's records only
  const getTodayAttendanceRecords = useCallback(() => {
    if (!Array.isArray(attendanceData)) return [];
    
    const today = selectedDate || new Date().toISOString().split('T')[0];
    return attendanceData.filter(record => record.date === today);
  }, [attendanceData, selectedDate]);

  // Update the statistics calculations
  const calculateStats = useCallback(() => {
    const todayRecords = getTodayAttendanceRecords();
    const total = teacherList.length;
    let present = 0;
    let absent = 0;
    let late = 0;

    todayRecords.forEach(record => {
      switch (record.status) {
        case 'Present': present++; break;
        case 'Absent': absent++; break;
        case 'Late': late++; break;
      }
    });

    // Add unmarked staff as absent
    const markedStaffIds = todayRecords.map((record) => record.staffId._id);
    const unmarkedCount = teacherList.filter(
      (teacher) => !markedStaffIds.some((id) => String(id) === String(teacher._id))
    ).length;
    absent += unmarkedCount;

    const percentage = total > 0 ? Math.round((present / total) * 100) : 0;

    // setAttendanceStats({ // This state was not defined in the original file, so this line is commented out
    //   total,
    //   present,
    //   absent,
    //   late,
    //   percentage
    // });
  }, [getTodayAttendanceRecords, teacherList]);

  // Fetch report filters
  const fetchReportFilters = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    try {
      const schoolSeg = encodeURIComponent(String(admin.schoolID).trim());
      const data = await fetchJsonFromApi(
        `/api/attendance/reports/filters/${schoolSeg}?year=${selectedYear}`
      );
      if (data.success) {
        setAvailableFilters(data.data);
      }
    } catch (error) {
      console.error("Error fetching report filters:", error);
    }
  }, [admin?.schoolID, selectedYear]);

  // Fetch report data
  const fetchReportData = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    setLoading(true);
    try {
      const schoolSeg = encodeURIComponent(String(admin.schoolID).trim());
      const params = new URLSearchParams({
        filter: reportFilter,
        year: String(selectedYear)
      });
      if (reportFilter === "week" && selectedWeek) {
        params.set("week", String(selectedWeek));
      } else if (reportFilter === "month" && selectedMonth) {
        params.set("month", String(selectedMonth));
      } else if (reportFilter === "term" && selectedTerm) {
        params.set("term", String(selectedTerm));
      }
      if (selectedStaff) {
        params.set("staffId", String(selectedStaff));
      }
      const data = await fetchJsonFromApi(
        `/api/attendance/reports/${schoolSeg}?${params.toString()}`
      );
      
      if (data.success) {
        setReportData(data.data);
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to fetch report data", severity: "error" });
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setSnackbar({ open: true, message: "Error fetching report data", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [admin?.schoolID, reportFilter, selectedWeek, selectedMonth, selectedTerm, selectedYear, selectedStaff]);

  // Fetch attendance records with filters
  const fetchAttendanceRecords = useCallback(async () => {
    if (!admin?.schoolID) return;
    
    setLoading(true);
    try {
      const schoolSeg = encodeURIComponent(String(admin.schoolID).trim());
      const params = new URLSearchParams();
      if (dateFilter.startDate) {
        params.append("startDate", dateFilter.startDate.toISOString());
      }
      if (dateFilter.endDate) {
        params.append("endDate", dateFilter.endDate.toISOString());
      }
      if (staffFilter) {
        params.append("staffId", String(staffFilter));
      }
      if (statusFilter) {
        params.append("status", statusFilter);
      }
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);
      const qs = params.toString();
      const path = `/api/attendance/records/${schoolSeg}${qs ? `?${qs}` : ""}`;
      const data = await fetchJsonFromApi(path);
      
      if (data.success) {
        setAttendanceRecords(Array.isArray(data.data) ? data.data : []);
        setFilteredRecords(Array.isArray(data.data) ? data.data : []);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to fetch attendance records",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error fetching attendance records",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [admin?.schoolID, dateFilter, staffFilter, statusFilter, sortBy, sortOrder]);

  // Load initial data
  useEffect(() => {
    fetchAttendanceData();
    fetchReportFilters();
  }, [fetchAttendanceData, fetchReportFilters]);

  // Load attendance records when filters change
  useEffect(() => {
    if (value === 2) {
      // Attendance Records tab
      fetchAttendanceRecords();
    }
  }, [fetchAttendanceRecords, value]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  // Reports tab: load when opened or when period filters change
  useEffect(() => {
    if (value !== 3 || !admin?.schoolID) return;
    fetchReportData();
  }, [
    value,
    admin?.schoolID,
    reportFilter,
    selectedWeek,
    selectedMonth,
    selectedTerm,
    selectedYear,
    selectedStaff,
    fetchReportData
  ]);

  // Handle manual attendance marking
  const handleManualAttendance = async () => {
    if (!selectedStaffForManual) {
      setSnackbar({ open: true, message: "Please select a staff member", severity: "error" });
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch("/api/attendance/mark-manual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolID: admin.schoolID,
          staffId: selectedStaffForManual,
          status: manualStatus,
          checkInTime: manualCheckIn,
          checkOutTime: manualCheckOut,
          remarks: manualRemarks,
          attendanceType: "Manual"
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: "Manual attendance marked successfully", severity: "success" });
        setManualDialogOpen(false);
        fetchAttendanceData();
        
        // Emit real-time update
        if (socket) {
          socket.emit("manualAttendanceMarked", {
            schoolID: admin.schoolID,
            staffId: selectedStaffForManual,
            status: manualStatus
          });
        }
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to mark attendance", severity: "error" });
      }
    } catch (error) {
      console.error("Error marking manual attendance:", error);
      setSnackbar({ open: true, message: "Error marking attendance", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Handle school location settings
  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/attendance/school-location", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolID: admin.schoolID,
          latitude: parseFloat(schoolLat),
          longitude: parseFloat(schoolLng),
          address: schoolAddress,
          allowedRadius: parseInt(allowedRadius),
          workingHours,
          lateThreshold: parseInt(lateThreshold),
          autoAttendance
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSnackbar({ open: true, message: "Settings saved successfully", severity: "success" });
        setSettingsDialogOpen(false);
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to save settings", severity: "error" });
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setSnackbar({ open: true, message: "Error saving settings", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Export report data
  const handleExportReport = () => {
    if (!reportData) return;
    
    const csvContent = generateCSV(reportData);
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff-attendance-report-${reportFilter}-${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Generate CSV content
  const generateCSV = (data) => {
    let csv =
      "Staff Name,Date,Status,Check In,Check Out,Working Hours,Attendance Type,Remarks\n";
    const rows = Array.isArray(data?.attendanceRecords)
      ? data.attendanceRecords
      : [];
    rows.forEach((record) => {
      const name = (record.staffId && record.staffId.name) || "";
      const dateCell =
        typeof record.date === "string"
          ? record.date
          : record.date
            ? format(new Date(record.date), "yyyy-MM-dd")
            : "";
      const cin = record.checkInTime
        ? format(new Date(record.checkInTime), "yyyy-MM-dd HH:mm")
        : "";
      const cout = record.checkOutTime
        ? format(new Date(record.checkOutTime), "yyyy-MM-dd HH:mm")
        : "";
      csv += `"${String(name).replace(/"/g, '""')}","${dateCell}","${record.status || ""}","${cin}","${cout}","${record.workingHours ?? 0}","${record.attendanceType || ""}","${String(record.remarks || "").replace(/"/g, '""')}"\n`;
    });

    return csv;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Present": return "success";
      case "Absent": return "error";
      case "Late": return "warning";
      case "On Leave": return "info";
      default: return "default";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "Present": return <CheckCircle />;
      case "Absent": return <Cancel />;
      case "Late": return <Schedule />;
      case "On Leave": return <BeachAccess />;
      default: return <AccessTime />;
    }
  };

  // Add this function for applying filters
  const applyFilters = useCallback(() => {
    let filtered = [...attendanceRecords];
    
    // Apply date filter
    if (dateFilter.startDate || dateFilter.endDate) {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        if (dateFilter.startDate && recordDate < dateFilter.startDate) return false;
        if (dateFilter.endDate && recordDate > dateFilter.endDate) return false;
        return true;
      });
    }
    
    // Apply staff filter
    if (staffFilter) {
      filtered = filtered.filter(
        (record) => String(record.staffId?._id) === String(staffFilter)
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(record => record.status === statusFilter);
    }
    
    setFilteredRecords(filtered);
  }, [attendanceRecords, dateFilter, staffFilter, statusFilter]);

  // Add this useEffect for applying filters
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Add this function for clearing filters
  const clearFilters = () => {
    setDateFilter({ startDate: null, endDate: null });
    setStaffFilter("");
    setStatusFilter("");
    setFilteredRecords(attendanceRecords);
  };

  // Add this function for exporting records
  const exportRecords = () => {
    const csvContent = generateAttendanceCSV(filteredRecords);
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-records-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Add this function for generating CSV
  const generateAttendanceCSV = (records) => {
    let csv = "Date,Staff Name,Position,Status,Check In,Check Out,Working Hours,Attendance Type,Remarks\n";
    
    records.forEach(record => {
      csv += `"${format(new Date(record.date), "yyyy-MM-dd")}","${record.staffId.name}","${record.staffId.position || ""}","${record.status}","${record.checkInTime ? format(new Date(record.checkInTime), "HH:mm") : ""}","${record.checkOutTime ? format(new Date(record.checkOutTime), "HH:mm") : ""}","${record.workingHours || 0}","${record.attendanceType}","${record.remarks || ""}"\n`;
    });
    
    return csv;
  };

  // Add missing function for using current location for school
  const useCurrentLocationForSchool = useCallback(async () => {
    if (!currentLocation) {
      setSnackbar({ open: true, message: "Please get your current location first", severity: "warning" });
      return;
    }
    
    setSchoolLat(currentLocation.latitude.toString());
    setSchoolLng(currentLocation.longitude.toString());
    setSchoolAddress(locationAddress);
    
    setSnackbar({ 
      open: true, 
      message: "School location updated with current location", 
      severity: "success" 
    });
  }, [currentLocation, locationAddress]);

  // Update the bulk actions function
  const applyBulkStaffAction = async () => {
    if (!bulkAction || selectedStaffForBulk.length === 0) return;

    setLoading(true);
    try {
      const currentTime = new Date().toISOString();
      
      // Process each staff member individually since there's no bulk endpoint
      const promises = selectedStaffForBulk.map(async (staffId) => {
        const response = await axios.post(buildApiUrl("/api/attendance/mark-manual"), {
        schoolID: admin.schoolID,
        staffId: staffId,
        status: bulkAction,
        date: selectedDate,
        time: currentTime,
        attendanceType: "Manual",
        remarks: `Bulk ${bulkAction.toLowerCase()} on ${selectedDate}`
        });
        return response;
      });

      await Promise.all(promises);

        setSnackbar({ 
          open: true, 
          message: `${selectedStaffForBulk.length} staff ${bulkAction.toLowerCase()} recorded`, 
          severity: "success" 
        });
        
        setBulkDialogOpen(false);
        setSelectedStaffForBulk([]);
        setSelectAll(false);
        
        // Refresh attendance data
        fetchAttendanceData();
    } catch (error) {
      console.error("Error in bulk staff attendance:", error);
      setSnackbar({ 
        open: true, 
        message: "Error in bulk operation", 
        severity: "error" 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle individual staff selection
  const handleStaffSelect = (staffId) => {
    const newSelected = selectedStaffForBulk.includes(staffId)
      ? selectedStaffForBulk.filter(id => id !== staffId)
      : [...selectedStaffForBulk, staffId];
    
    setSelectedStaffForBulk(newSelected);
    setSelectAll(newSelected.length === teacherList.length);
  };

  // Handle select all staff
  const handleSelectAllStaff = () => {
    if (selectAll) {
      setSelectedStaffForBulk([]);
    } else {
      setSelectedStaffForBulk(teacherList.map((teacher) => teacher._id));
    }
    setSelectAll(!selectAll);
  };

  // Update the getStaffAttendanceStatus function
  const getStaffAttendanceStatus = (staffId) => {
    const todayRecords = getTodayAttendanceRecords();
    const todayRecord = todayRecords.find(record => 
      record.staffId._id === staffId
    );
    
    return todayRecord ? todayRecord.status : 'unmarked';
  };

  // Update the getStaffCheckStatus function
  const getStaffCheckStatus = (staffId) => {
    const todayRecords = getTodayAttendanceRecords();
    const todayRecord = todayRecords.find(record => 
      record.staffId._id === staffId
    );
    
    if (!todayRecord) return { checkIn: false, checkOut: false };
    
    return {
      checkIn: !!todayRecord.checkInTime,
      checkOut: !!todayRecord.checkOutTime
    };
  };

  // Add useEffect to calculate stats when data changes
  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  // Filter function for dashboard records
  const filterDashboardRecords = useCallback(() => {
    if (!Array.isArray(attendanceData)) {
      setFilteredDashboardRecords([]);
      return;
    }

    let filtered = [...attendanceData];

    // Filter by status
    if (dashboardFilter.status) {
      filtered = filtered.filter(record => record.status === dashboardFilter.status);
    }

    // Filter by department
    if (dashboardFilter.department) {
      filtered = filtered.filter(record => 
        record.staffId?.position?.toLowerCase().includes(dashboardFilter.department.toLowerCase())
      );
    }

    // Filter by search term (name)
    if (dashboardFilter.search) {
      filtered = filtered.filter(record => 
        record.staffId?.name?.toLowerCase().includes(dashboardFilter.search.toLowerCase())
      );
    }

    setFilteredDashboardRecords(filtered);
  }, [attendanceData, dashboardFilter]);

  // Update filtered records when data or filters change
  useEffect(() => {
    filterDashboardRecords();
  }, [filterDashboardRecords]);

  // Clear dashboard filters
  const clearDashboardFilters = () => {
    setDashboardFilter({
      status: '',
      department: '',
      search: ''
    });
  };

  // Get unique departments for filter
  const getUniqueDepartments = () => {
    if (!Array.isArray(attendanceData)) return [];
    const departments = attendanceData
      .map(record => record.staffId?.position)
      .filter(Boolean)
      .filter((dept, index, arr) => arr.indexOf(dept) === index);
    return departments;
  };

  // Function to handle view details
  const handleViewDetails = (record) => {
    console.log('View Details clicked, record:', record);
    setSelectedRecord(record);
    setViewDetailsDialog(true);
    
    // Set default date range (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    
    setViewDetailsFilter({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      status: ''
    });
    
    // Load staff attendance history from existing data
    loadStaffAttendanceHistory(record.staffId._id);
  };

  // Function to load staff attendance history
  const loadStaffAttendanceHistory = async (staffId) => {
    if (!staffId || !admin?.schoolID) return;
    
    try {
      // First try to get data from existing attendanceData
      if (Array.isArray(attendanceData)) {
        const staffRecords = attendanceData.filter(record => 
          record.staffId && record.staffId._id === staffId
        );
        
        if (staffRecords.length > 0) {
          setStaffAttendanceHistory(staffRecords);
          return;
        }
      }
      
      // If no data in attendanceData, try to fetch from API
      const response = await fetch(`https://servermain.sisrevolution.com/api/attendance/staff/${staffId}/attendance?schoolID=${admin.schoolID}`);
      const data = await response.json();
      
      if (data.success && data.data) {
        setStaffAttendanceHistory(data.data.attendanceRecords || []);
      } else {
        // Fallback: create a mock record from the current selection
        if (selectedRecord) {
          setStaffAttendanceHistory([selectedRecord]);
        } else {
          setStaffAttendanceHistory([]);
        }
      }
    } catch (error) {
      console.error('Error loading staff attendance history:', error);
      // Fallback: use the selected record
      if (selectedRecord) {
        setStaffAttendanceHistory([selectedRecord]);
      } else {
        setStaffAttendanceHistory([]);
      }
    }
  };

  // Function to filter staff records
  const filterStaffRecords = useCallback(() => {
    console.log('Filtering staff records...');
    console.log('staffAttendanceHistory:', staffAttendanceHistory);
    console.log('viewDetailsFilter:', viewDetailsFilter);
    
    if (!Array.isArray(staffAttendanceHistory) || staffAttendanceHistory.length === 0) {
      console.log('No staff attendance history available');
      setFilteredStaffRecords([]);
      return;
    }

    let filtered = [...staffAttendanceHistory];
    console.log('Initial filtered records:', filtered.length);

    // Filter by date range
    if (viewDetailsFilter.startDate) {
      const startDate = new Date(viewDetailsFilter.startDate);
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate >= startDate;
      });
      console.log('After start date filter:', filtered.length);
    }
    
    if (viewDetailsFilter.endDate) {
      const endDate = new Date(viewDetailsFilter.endDate);
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate <= endDate;
      });
      console.log('After end date filter:', filtered.length);
    }

    // Filter by status
    if (viewDetailsFilter.status) {
      filtered = filtered.filter(record => record.status === viewDetailsFilter.status);
      console.log('After status filter:', filtered.length);
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

    console.log('Final filtered records:', filtered.length);
    setFilteredStaffRecords(filtered);
  }, [staffAttendanceHistory, viewDetailsFilter]);

  // Add a useEffect to trigger filtering when data changes
  useEffect(() => {
    if (staffAttendanceHistory.length > 0) {
      filterStaffRecords();
    }
  }, [staffAttendanceHistory, filterStaffRecords]);

  // Clear view details filters
  const clearViewDetailsFilters = () => {
    setViewDetailsFilter({
      startDate: '',
      endDate: '',
      status: ''
    });
  };

  // Calculate attendance statistics for the filtered period
  const calculateStaffStats = () => {
    const total = filteredStaffRecords.length;
    const present = filteredStaffRecords.filter(r => r.status === 'Present').length;
    const absent = filteredStaffRecords.filter(r => r.status === 'Absent').length;
    const late = filteredStaffRecords.filter(r => r.status === 'Late').length;
    const halfDay = filteredStaffRecords.filter(r => r.status === 'Half Day').length;
    const onLeave = filteredStaffRecords.filter(r => r.status === 'On Leave').length;
    
    const attendanceRate = total > 0 ? Math.round((present / total) * 100) : 0;
    
    return {
      total,
      present,
      absent,
      late,
      halfDay,
      onLeave,
      attendanceRate
    };
  };

  // Function to close view details dialog
  const handleCloseViewDetails = () => {
    setViewDetailsDialog(false);
    setSelectedRecord(null);
  };

  // Add the test function at the top of the component
  const testMarkAbsent = (staffId) => {
    console.log('TEST: Mark Absent clicked for staff:', staffId);
    alert(`Test: Mark Absent clicked for staff ${staffId}`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Environment Indicator */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Environment:</strong> {isElectron ? 'Electron Desktop App' : 'Web Browser'} | 
            <strong> Status:</strong> {isOnline ? 'Online' : 'Offline'} | 
            <strong> Location:</strong> {locationPermission === "granted" ? "Available" : locationPermission === "denied" ? "Denied" : "Not Requested"}
          </Typography>
        </Alert>

        {/* Location Permission Error Alert */}
        {locationError && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
                  size="small" 
                  onClick={requestLocationPermission}
                  disabled={locationLoading}
                >
                  {locationLoading ? <CircularProgress size={16} /> : "Retry"}
            </Button>
            <Button
                  size="small" 
                  onClick={enableManualLocationEntry}
            >
              Manual Entry
            </Button>
          </Box>
            }
          >
            <Typography variant="body2">
              <strong>Location Error:</strong> {locationError}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {isElectron ? (
                <>
                  To enable location access in Electron:
                  <br />• Check system location services are enabled
                  <br />• Ensure the app has location permissions
                  <br />• Restart the application
                  <br />• Or use manual location entry as a fallback
                </>
              ) : (
                <>
                  To enable location access in browser:
                  <br />• Click the location icon in your browser's address bar
                  <br />• Select "Allow" for location access
                  <br />• Refresh the page
                  <br />• Or use manual location entry as a fallback
                </>
              )}
            </Typography>
          </Alert>
        )}

        {/* GPS Location Settings Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                📍 GPS Location Settings {isElectron && "(Electron)"}
              </Typography>
              <Chip 
                label={
                  locationPermission === "granted" ? "Connected" : 
                  locationPermission === "denied" ? "Denied" : 
                  locationPermission === "manual" ? "Manual" : "Not Available"
                }
                color={
                  locationPermission === "granted" ? "success" : 
                  locationPermission === "denied" ? "error" : 
                  locationPermission === "manual" ? "warning" : "default"
                }
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useGPS}
                    onChange={(e) => {
                      setUseGPS(e.target.checked);
                      if (e.target.checked) {
                        requestLocationPermission();
                      }
                    }}
                    color="primary"
                  />
                }
                label={`Use Current Location ${isElectron ? '(Electron)' : '(Browser)'}`}
              />
              {locationLoading && <CircularProgress size={20} />}
              <Button
                size="small"
                variant="outlined"
                startIcon={<MyLocation />}
                onClick={requestLocationPermission}
                disabled={locationLoading}
              >
                {locationLoading ? "Getting Location..." : "Refresh Location"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LocationOn />}
                onClick={enableManualLocationEntry}
              >
                Manual Entry
              </Button>
            </Box>
            
            {currentLocation && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Current Location:</strong> {locationAddress}
                </Typography>
                <Typography variant="caption" display="block">
                  Accuracy: ±{Math.round(locationAccuracy)}m | 
                  Environment: {isElectron ? 'Electron' : 'Browser'} | 
                  Timestamp: {new Date(currentLocation.timestamp).toLocaleString()}
                </Typography>
              </Box>
            )}

            {/* Manual Location Entry Form */}
            {locationPermission === "manual" && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Manual Location Entry
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Latitude"
                      type="number"
                      value={schoolLat}
                      onChange={(e) => setSchoolLat(e.target.value)}
                      inputProps={{ step: 'any' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Longitude"
                      type="number"
                      value={schoolLng}
                      onChange={(e) => setSchoolLng(e.target.value)}
                      inputProps={{ step: 'any' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address"
                      value={schoolAddress}
                      onChange={(e) => setSchoolAddress(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        <Paper sx={{ width: "100%" }}>
          <Tabs
            value={value}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Dashboard" />
            <Tab label="Staff Attendance" />
            <Tab label="Attendance Records" />
            <Tab label="Reports" />
          </Tabs>

          <TabPanel value={value} index={0}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Statistics Cards */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ bgcolor: "primary.light", p: 1, borderRadius: 1 }}>
                          <Person color="primary" sx={{ fontSize: 40 }} />
                        </Box>
                        <Box>
                          <Typography variant="h4" component="div">
                            {attendanceData.length}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Total Staff
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ bgcolor: "success.light", p: 1, borderRadius: 1 }}>
                          <CheckCircle color="success" sx={{ fontSize: 40 }} />
                        </Box>
                        <Box>
                          <Typography variant="h4" component="div">
                            {attendanceData.filter(record => record.status === "Present").length}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Present Today
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ bgcolor: "error.light", p: 1, borderRadius: 1 }}>
                          <Cancel color="error" sx={{ fontSize: 40 }} />
                        </Box>
                        <Box>
                          <Typography variant="h4" component="div">
                            {attendanceData.filter(record => record.status === "Absent").length}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Absent Today
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ bgcolor: "warning.light", p: 1, borderRadius: 1 }}>
                          <Schedule color="warning" sx={{ fontSize: 40 }} />
                        </Box>
                        <Box>
                          <Typography variant="h4" component="div">
                            {attendanceData.filter(record => record.status === "Late").length}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Late Today
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* GPS Status */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <MyLocation color="primary" />
                        <Typography variant="h6">GPS Attendance Status</Typography>
                        <Chip 
                          label={schooldetails?.gpsEnabled ? "Enabled" : "Disabled"} 
                          color={schooldetails?.gpsEnabled ? "success" : "error"} 
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        School Location: {schooldetails?.address || "Not configured"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Allowed Radius: {schooldetails?.allowedRadius || 100}m
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Today's Attendance Records with Filtering */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Today&apos;s Attendance Records
                      </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={filterDashboardRecords}
                          >
                            Apply Filters
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={clearDashboardFilters}
                          >
                            Clear
                          </Button>
                        </Box>
                      </Box>

                      {/* Filter Controls */}
                      <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
                        <CardContent>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Search Staff"
                                value={dashboardFilter.search}
                                onChange={(e) => setDashboardFilter(prev => ({ ...prev, search: e.target.value }))}
                                InputProps={{
                                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                  value={dashboardFilter.status}
                                  onChange={(e) => setDashboardFilter(prev => ({ ...prev, status: e.target.value }))}
                                  label="Status"
                                >
                                  <MenuItem value="">All Status</MenuItem>
                                  <MenuItem value="Present">Present</MenuItem>
                                  <MenuItem value="Absent">Absent</MenuItem>
                                  <MenuItem value="Late">Late</MenuItem>
                                  <MenuItem value="Half Day">Half Day</MenuItem>
                                  <MenuItem value="On Leave">On Leave</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Department</InputLabel>
                                <Select
                                  value={dashboardFilter.department}
                                  onChange={(e) => setDashboardFilter(prev => ({ ...prev, department: e.target.value }))}
                                  label="Department"
                                >
                                  <MenuItem value="">All Departments</MenuItem>
                                  {getUniqueDepartments().map(dept => (
                                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Typography variant="body2" color="textSecondary">
                                Showing {filteredDashboardRecords.length} of {attendanceData.length} records
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Staff</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Check In</TableCell>
                              <TableCell>Check Out</TableCell>
                              <TableCell>Working Hours</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {loading ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  <CircularProgress />
                                </TableCell>
                              </TableRow>
                            ) : filteredDashboardRecords.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  {attendanceData.length === 0 ? 
                                    "No attendance data available" : 
                                    "No records match the current filters"
                                  }
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredDashboardRecords.map((record, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Avatar sx={{ width: 32, height: 32 }}>
                                        {record.staffId?.name?.charAt(0) || "?"}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          {record.staffId?.name || "Unknown"}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          {record.staffId?.position || ""}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      icon={getStatusIcon(record.status)}
                                      label={record.status}
                                      color={getStatusColor(record.status)}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {record.checkInTime ? new Date(record.checkInTime).toLocaleTimeString() : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {record.checkOutTime ? new Date(record.checkOutTime).toLocaleTimeString() : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {record.workingHours ? `${record.workingHours.toFixed(1)}h` : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={record.attendanceType || "Manual"}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="View Details">
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleViewDetails(record)}
                                      >
                                        <Visibility />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                      <IconButton size="small">
                                        <Edit />
                                      </IconButton>
                                    </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* NEW TAB: Staff Attendance Marking */}
          <TabPanel value={value} index={1}>
            <Box sx={{ p: 3 }}>
              {/* Date Selection and Controls */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Date"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setBulkDialogOpen(true)}
                          disabled={selectedStaffForBulk.length === 0}
                          color="primary"
                        >
                          Bulk Actions ({selectedStaffForBulk.length})
                        </Button> */}
                        <Button
                          variant="outlined"
                          startIcon={<Refresh />}
                          onClick={fetchAttendanceData}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Selected: {selectedStaffForBulk.length} of {teacherList.length} staff
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Staff Attendance Marking Table */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Staff Attendance - {format(new Date(selectedDate), "MMMM dd, yyyy")}
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedStaffForBulk.length > 0 && selectedStaffForBulk.length < teacherList.length}
                              checked={selectAll && teacherList.length > 0}
                              onChange={handleSelectAllStaff}
                            />
                          </TableCell>
                          <TableCell>Photo</TableCell>
                          <TableCell>Staff Name</TableCell>
                          <TableCell>Position</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Check In</TableCell>
                          <TableCell>Check Out</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : teacherList.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No staff data available
                            </TableCell>
                          </TableRow>
                        ) : (
                          teacherList.map((teacher) => {
                            const status = getStaffAttendanceStatus(teacher._id);
                            const isSelected = selectedStaffForBulk.includes(teacher._id);
                            const isLoading = markingLoading[teacher._id];
                            
                            return (
                              <TableRow
                                key={teacher._id}
                                selected={isSelected}
                                hover
                                sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleStaffSelect(teacher._id)}
                                  />
                                  
                                </TableCell>
                                <TableCell>
                                  <Avatar
                                    src={mediaUrl('thumbnail', teacher.image)}
                                    alt={teacher.lastname + " " + teacher.othernames}
                                    sx={{ width: 40, height: 40 }}
                                  >
                                    <Person />
                                  </Avatar>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1" fontWeight="medium">
                                    {teacher.lastname?.toUpperCase() + " " + teacher.othernames?.toUpperCase()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={teacher.position || "Staff"}
                                    size="small"
                                    color="default"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={status.toUpperCase()}
                                    color={
                                      status === 'Present' ? 'success' :
                                      status === 'Absent' ? 'error' : 
                                      status === 'Late' ? 'warning' : 'default'
                                    }
                                    variant={status === 'unmarked' ? 'outlined' : 'filled'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {attendanceData.find(record => 
                                    record.staffId._id === teacher._id && 
                                    new Date(record.date).toISOString().split('T')[0] === selectedDate
                                  )?.checkInTime ? 
                                    format(new Date(attendanceData.find(record => 
                                      record.staffId._id === teacher._id && 
                                      new Date(record.date).toISOString().split('T')[0] === selectedDate
                                    ).checkInTime), "HH:mm") : "-"
                                  }
                                </TableCell>
                                <TableCell>
                                  {attendanceData.find(record => 
                                    record.staffId._id === teacher._id && 
                                    new Date(record.date).toISOString().split('T')[0] === selectedDate
                                  )?.checkOutTime ? 
                                    format(new Date(attendanceData.find(record => 
                                      record.staffId._id === teacher._id && 
                                      new Date(record.date).toISOString().split('T')[0] === selectedDate
                                    ).checkOutTime), "HH:mm") : "-"
                                  }
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  
                                  
                                   <Tooltip title="Check In">
                                      <IconButton
                                        color="success"
                                        onClick={() => markStaffCheckIn(teacher._id)}
                                        // disabled={getStaffCheckStatus(teacher._id).checkIn || isLoading}
                                        size="small"
                                      >
                                        {isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
                                      </IconButton>
                                    </Tooltip>



                                    <Tooltip title="Check Out">
                                      <IconButton
                                        color="error"
                                        onClick={() => markStaffCheckOut(teacher._id)}
                                        disabled={getStaffCheckStatus(teacher._id).checkOut || isLoading}
                                        size="small"
                                      >
                                        {isLoading ? <CircularProgress size={20} /> : <LogoutIcon />}
                                      </IconButton>
                                    </Tooltip>
                                  
                                    <Tooltip title="Mark Absent">
                                      <IconButton
                                        color="warning"
                                        onClick={() => {
                                          console.log('Mark Absent button clicked for staff:', teacher._id);
                                          console.log('Current status:', status);
                                          console.log('Is loading:', isLoading);
                                          markStaffAttendance(teacher._id, 'Absent');
                                        }}
                                        disabled={status === 'Absent' || isLoading}
                                        size="small"
                                      >
                                        {isLoading ? <CircularProgress size={20} /> : <Cancel />}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>

          <TabPanel value={value} index={2}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Attendance Records
              </Typography>
              
              {/* Filter Controls */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                      <DatePicker
                        label="Start Date"
                        value={dateFilter.startDate}
                        onChange={(newValue) => setDateFilter(prev => ({ ...prev, startDate: newValue }))}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={2}>
                      <DatePicker
                        label="End Date"
                        value={dateFilter.endDate}
                        onChange={(newValue) => setDateFilter(prev => ({ ...prev, endDate: newValue }))}
                        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Staff Member</InputLabel>
                        <Select
                          value={staffFilter}
                          onChange={(e) => setStaffFilter(e.target.value)}
                          label="Staff Member"
                        >
                          <MenuItem value="">All Staff</MenuItem>
                          {teacherList.map((teacher) => (
                            <MenuItem key={teacher._id} value={teacher._id}>
                              {teacherDisplayName(teacher)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="">All Status</MenuItem>
                          <MenuItem value="Present">Present</MenuItem>
                          <MenuItem value="Absent">Absent</MenuItem>
                          <MenuItem value="Late">Late</MenuItem>
                          <MenuItem value="On Leave">On Leave</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sort By</InputLabel>
                        <Select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          label="Sort By"
                        >
                          <MenuItem value="date">Date</MenuItem>
                          <MenuItem value="staffName">Staff Name</MenuItem>
                          <MenuItem value="status">Status</MenuItem>
                          <MenuItem value="checkInTime">Check In Time</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Order</InputLabel>
                        <Select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          label="Order"
                        >
                          <MenuItem value="desc">Newest First</MenuItem>
                          <MenuItem value="asc">Oldest First</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={applyFilters}
                      size="small"
                    >
                      Apply Filters
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={clearFilters}
                      size="small"
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={exportRecords}
                      size="small"
                      disabled={filteredRecords.length === 0}
                    >
                      Export CSV
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              
              {/* Results Summary */}
              <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  Showing {filteredRecords.length} of {attendanceRecords.length} records
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Last updated: {format(new Date(), "MMM dd, yyyy HH:mm")}
                </Typography>
              </Box>
            </Box>

            {/* Attendance Records Table */}
            <Card>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Staff Member</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Check In</TableCell>
                        <TableCell>Check Out</TableCell>
                        <TableCell>Working Hours</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : filteredRecords.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Box sx={{ py: 4 }}>
                              <Typography variant="body1" color="textSecondary">
                                No attendance records found
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Try adjusting your filters or check back later
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRecords.map((record, index) => (
                          <TableRow key={record._id || index} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {format(new Date(record.date), "MMM dd, yyyy")}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {format(new Date(record.date), "EEEE")}
                              </Typography>
                            </TableCell>
                            
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {record.staffId?.name?.charAt(0) || "?"}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {record.staffId?.name || "Unknown"}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {record.staffId?.position || ""}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            
                            <TableCell>
                              <Chip
                                icon={getStatusIcon(record.status)}
                                label={record.status}
                                color={getStatusColor(record.status)}
                                size="small"
                              />
                            </TableCell>
                            
                            <TableCell>
                              {record.checkInTime ? (
                                <Box>
                                  <Typography variant="body2">
                                    {format(new Date(record.checkInTime), "HH:mm")}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {format(new Date(record.checkInTime), "MMM dd")}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            
                            <TableCell>
                              {record.checkOutTime ? (
                                <Box>
                                  <Typography variant="body2">
                                    {format(new Date(record.checkOutTime), "HH:mm")}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {format(new Date(record.checkOutTime), "MMM dd")}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            
                            <TableCell>
                              {record.workingHours ? (
                                <Typography variant="body2" fontWeight="bold">
                                  {record.workingHours.toFixed(1)}h
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            
                            <TableCell>
                              <Chip
                                label={record.attendanceType || "Manual"}
                                size="small"
                                variant="outlined"
                                color={record.attendanceType === "GPS" ? "primary" : "default"}
                              />
                            </TableCell>
                            
                            <TableCell>
                              {record.remarks ? (
                                <Tooltip title={record.remarks}>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                                    {record.remarks}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title="View Details">
                                  <IconButton size="small">
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Record">
                                  <IconButton size="small">
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={value} index={3}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Assessment color="primary" />
                Reports & Analytics
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Choose a period, review summary metrics and per-staff breakdown,
                then export detailed rows as CSV.
              </Typography>
            </Box>

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Period type</InputLabel>
                      <Select
                        value={reportFilter}
                        label="Period type"
                        onChange={(e) => {
                          const v = e.target.value;
                          setReportFilter(v);
                          if (v === "term" && !selectedTerm) {
                            setSelectedTerm("1");
                          }
                          if (v === "month" && !selectedMonth) {
                            setSelectedMonth(String(new Date().getMonth() + 1));
                          }
                        }}
                      >
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                        <MenuItem value="term">Term</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Year"
                      value={selectedYear}
                      onChange={(e) =>
                        setSelectedYear(parseInt(e.target.value, 10) || new Date().getFullYear())
                      }
                    />
                  </Grid>
                  {reportFilter === "week" && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Week</InputLabel>
                        <Select
                          value={selectedWeek}
                          label="Week"
                          onChange={(e) => setSelectedWeek(e.target.value)}
                        >
                          <MenuItem value="">
                            Default (current week in range logic)
                          </MenuItem>
                          {(availableFilters.weeks || []).map((w) => (
                            <MenuItem key={w.week} value={String(w.week)}>
                              {w.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {reportFilter === "month" && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Month</InputLabel>
                        <Select
                          value={selectedMonth}
                          label="Month"
                          onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                          <MenuItem value="">
                            Default (current week in range logic)
                          </MenuItem>
                          {(availableFilters.months || []).map((m) => (
                            <MenuItem key={m.month} value={String(m.month)}>
                              {m.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {reportFilter === "term" && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Term</InputLabel>
                        <Select
                          value={selectedTerm}
                          label="Term"
                          onChange={(e) => setSelectedTerm(e.target.value)}
                        >
                          <MenuItem value="">Select term</MenuItem>
                          {(availableFilters.terms || []).map((t) => (
                            <MenuItem key={t.term} value={String(t.term)}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Staff (optional)</InputLabel>
                      <Select
                        value={selectedStaff}
                        label="Staff (optional)"
                        onChange={(e) => setSelectedStaff(e.target.value)}
                      >
                        <MenuItem value="">All staff</MenuItem>
                        {teacherList.map((teacher) => (
                          <MenuItem key={teacher._id} value={teacher._id}>
                            {teacherDisplayName(teacher)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={() => fetchReportFilters()}
                      disabled={loading}
                    >
                      Refresh periods
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={handleExportReport}
                      disabled={!reportData || loading}
                    >
                      Export CSV
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {loading && !reportData && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {reportData && (
              <>
                {reportData.dateRange?.start && reportData.dateRange?.end && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Range:{" "}
                    {format(
                      new Date(reportData.dateRange.start),
                      "MMM d, yyyy"
                    )}{" "}
                    —{" "}
                    {format(
                      new Date(reportData.dateRange.end),
                      "MMM d, yyyy"
                    )}{" "}
                    · Filter: {reportData.filter || reportFilter}
                  </Typography>
                )}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    {
                      label: "Total records",
                      value: reportData.summary?.totalRecords ?? 0,
                      icon: <FilterList color="action" />
                    },
                    {
                      label: "Present",
                      value: reportData.summary?.present ?? 0,
                      icon: <CheckCircle color="success" />
                    },
                    {
                      label: "Absent",
                      value: reportData.summary?.absent ?? 0,
                      icon: <Cancel color="error" />
                    },
                    {
                      label: "Late",
                      value: reportData.summary?.late ?? 0,
                      icon: <Schedule color="warning" />
                    },
                    {
                      label: "On leave",
                      value: reportData.summary?.onLeave ?? 0,
                      icon: <BeachAccess color="info" />
                    },
                    {
                      label: "Avg. hours / record",
                      value:
                        (reportData.summary?.averageWorkingHours != null
                          ? reportData.summary.averageWorkingHours.toFixed(1)
                          : "0.0") + " h",
                      icon: <TrendingUp color="primary" />
                    }
                  ].map((card) => (
                    <Grid item xs={12} sm={6} md={4} key={card.label}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5
                            }}
                          >
                            {card.icon}
                            <Typography variant="caption" color="textSecondary">
                              {card.label}
                            </Typography>
                          </Box>
                          <Typography variant="h5">{card.value}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Per-staff summary
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 3, maxHeight: 320 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Staff</TableCell>
                        <TableCell align="right">Days</TableCell>
                        <TableCell align="right">Present</TableCell>
                        <TableCell align="right">Absent</TableCell>
                        <TableCell align="right">Late</TableCell>
                        <TableCell align="right">On leave</TableCell>
                        <TableCell align="right">Avg. hrs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(!reportData.staffBreakdown ||
                        reportData.staffBreakdown.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="textSecondary">
                              No staff rows for this period.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {(reportData.staffBreakdown || []).map((row, idx) => (
                        <TableRow
                          key={
                            row.staff?._id != null
                              ? String(row.staff._id)
                              : `staff-${idx}`
                          }
                        >
                          <TableCell>
                            {row.staff?.name || "—"}
                            {row.staff?.position ? (
                              <Typography
                                variant="caption"
                                display="block"
                                color="textSecondary"
                              >
                                {row.staff.position}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{row.totalDays ?? 0}</TableCell>
                          <TableCell align="right">{row.present ?? 0}</TableCell>
                          <TableCell align="right">{row.absent ?? 0}</TableCell>
                          <TableCell align="right">{row.late ?? 0}</TableCell>
                          <TableCell align="right">{row.onLeave ?? 0}</TableCell>
                          <TableCell align="right">
                            {(row.averageWorkingHours != null
                              ? row.averageWorkingHours
                              : 0
                            ).toFixed(1)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Detailed records
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Staff</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Check in</TableCell>
                        <TableCell>Check out</TableCell>
                        <TableCell align="right">Hrs</TableCell>
                        <TableCell>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(!reportData.attendanceRecords ||
                        reportData.attendanceRecords.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="textSecondary">
                              No attendance rows in this date range.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {(reportData.attendanceRecords || []).map((record) => (
                        <TableRow key={record._id || `${record.date}-${record.staffId?._id}`}>
                          <TableCell>
                            {typeof record.date === "string"
                              ? record.date
                              : format(new Date(record.date), "yyyy-MM-dd")}
                          </TableCell>
                          <TableCell>{record.staffId?.name || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={record.status || "—"}
                              color={getStatusColor(record.status)}
                            />
                          </TableCell>
                          <TableCell>
                            {record.checkInTime
                              ? format(new Date(record.checkInTime), "HH:mm")
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {record.checkOutTime
                              ? format(new Date(record.checkOutTime), "HH:mm")
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {record.workingHours != null
                              ? Number(record.workingHours).toFixed(1)
                              : "—"}
                          </TableCell>
                          <TableCell>{record.attendanceType || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {!loading && !reportData && admin?.schoolID && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No report loaded yet. Adjust filters above; data loads when this
                tab is open.
              </Alert>
            )}
          </TabPanel>
        </Paper>

        {/* Manual Attendance Dialog */}
        <Dialog open={manualDialogOpen} onClose={() => setManualDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Manual Attendance Entry</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Staff</InputLabel>
                  <Select
                    value={selectedStaffForManual}
                    onChange={(e) => setSelectedStaffForManual(e.target.value)}
                    label="Select Staff"
                  >
                    {teacherList.map((teacher) => (
                      <MenuItem key={teacher._id} value={teacher._id}>
                        {teacherDisplayName(teacher)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={manualStatus}
                    onChange={(e) => setManualStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Present">Present</MenuItem>
                    <MenuItem value="Absent">Absent</MenuItem>
                    <MenuItem value="Late">Late</MenuItem>
                    <MenuItem value="On Leave">On Leave</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={manualRemarks}
                  onChange={(e) => setManualRemarks(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleManualAttendance} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Mark Attendance"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>GPS Location Settings</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="School Latitude"
                  type="number"
                  value={schoolLat}
                  onChange={(e) => setSchoolLat(e.target.value)}
                  placeholder="e.g., 6.5244"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="School Longitude"
                  type="number"
                  value={schoolLng}
                  onChange={(e) => setSchoolLng(e.target.value)}
                  placeholder="e.g., 3.3792"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<MyLocation />}
                    onClick={useCurrentLocationForSchool}
                    disabled={locationLoading}
                  >
                    Use Current Location
                  </Button>
                  {locationLoading && <CircularProgress size={20} />}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School Address"
                  value={schoolAddress}
                  onChange={(e) => setSchoolAddress(e.target.value)}
                  placeholder="Enter complete school address"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Allowed Radius (meters)"
                  type="number"
                  value={allowedRadius}
                  onChange={(e) => setAllowedRadius(e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Late Threshold (minutes)"
                  type="number"
                  value={lateThreshold}
                  onChange={(e) => setLateThreshold(e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Working Hours Start"
                  type="time"
                  value={workingHours.start}
                  onChange={(e) => setWorkingHours({...workingHours, start: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Working Hours End"
                  type="time"
                  value={workingHours.end}
                  onChange={(e) => setWorkingHours({...workingHours, end: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoAttendance}
                      onChange={(e) => setAutoAttendance(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Enable Automatic GPS Attendance"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Save Settings"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Actions Dialog */}
        <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Bulk Staff Attendance Actions
            <Typography variant="body2" color="textSecondary">
              {selectedStaffForBulk.length} staff selected
            </Typography>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <MenuItem value="Check In">Check In All</MenuItem>
                <MenuItem value="Check Out">Check Out All</MenuItem>
                <MenuItem value="Present">Mark All Present</MenuItem>
                <MenuItem value="Absent">Mark All Absent</MenuItem>
                <MenuItem value="Late">Mark All Late</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={applyBulkStaffAction} 
              variant="contained"
              disabled={!bulkAction || selectedStaffForBulk.length === 0}
            >
              Apply to {selectedStaffForBulk.length} staff
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* View Details Dialog */}
        <Dialog 
          open={viewDetailsDialog} 
          onClose={handleCloseViewDetails} 
          maxWidth="lg" 
          fullWidth
          maxHeight="90vh"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 50, height: 50 }}>
                  {selectedRecord?.staffId?.name?.charAt(0) || "?"}
                </Avatar>
                <Box>
                  <Typography variant="h5">
                    {selectedRecord?.staffId?.name || "Unknown Staff"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedRecord?.staffId?.position || ""}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedRecord?.staffId?.email || ""}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseViewDetails}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {selectedRecord && (
              <Box>
                {/* Filter Controls */}
                <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Filter Attendance History
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start Date"
                          type="date"
                          value={viewDetailsFilter.startDate}
                          onChange={(e) => {
                            setViewDetailsFilter(prev => ({ ...prev, startDate: e.target.value }));
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End Date"
                          type="date"
                          value={viewDetailsFilter.endDate}
                          onChange={(e) => {
                            setViewDetailsFilter(prev => ({ ...prev, endDate: e.target.value }));
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={viewDetailsFilter.status}
                            onChange={(e) => {
                              setViewDetailsFilter(prev => ({ ...prev, status: e.target.value }));
                            }}
                            label="Status"
                          >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                            <MenuItem value="Late">Late</MenuItem>
                            <MenuItem value="Half Day">Half Day</MenuItem>
                            <MenuItem value="On Leave">On Leave</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              filterStaffRecords();
                            }}
                          >
                            Apply
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              clearViewDetailsFilters();
                            }}
                          >
                            Clear
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Statistics Cards */}
                {(() => {
                  const stats = calculateStaffStats();
                  return (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6">{stats.total}</Typography>
                            <Typography variant="body2" color="textSecondary">Total Days</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="success.main">{stats.present}</Typography>
                            <Typography variant="body2" color="textSecondary">Present</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="error.main">{stats.absent}</Typography>
                            <Typography variant="body2" color="textSecondary">Absent</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="warning.main">{stats.late}</Typography>
                            <Typography variant="body2" color="textSecondary">Late</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="info.main">{stats.halfDay}</Typography>
                            <Typography variant="body2" color="textSecondary">Half Day</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" color="primary.main">{stats.attendanceRate}%</Typography>
                            <Typography variant="body2" color="textSecondary">Attendance Rate</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  );
                })()}

                {/* Attendance History Table */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Attendance History ({filteredStaffRecords.length} records)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Check In</TableCell>
                            <TableCell>Check Out</TableCell>
                            <TableCell>Working Hours</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Remarks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredStaffRecords.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                No attendance records found for the selected period
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredStaffRecords.map((record, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="bold">
                                    {new Date(record.date).toLocaleDateString()}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {new Date(record.date).toLocaleDateString('en-US', { weekday: 'long' })}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={getStatusIcon(record.status)}
                                    label={record.status}
                                    color={getStatusColor(record.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {record.checkInTime ? 
                                    new Date(record.checkInTime).toLocaleTimeString() : 
                                    "-"
                                  }
                                </TableCell>
                                <TableCell>
                                  {record.checkOutTime ? 
                                    new Date(record.checkOutTime).toLocaleTimeString() : 
                                    "-"
                                  }
                                </TableCell>
                                <TableCell>
                                  {record.workingHours ? 
                                    `${record.workingHours.toFixed(1)}h` : 
                                    "-"
                                  }
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={record.attendanceType || "Manual"}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                    {record.remarks || "-"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseViewDetails}>
              Close
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCloseViewDetails}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}

export default StaffAttendance;
